import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../common';
import SearchBar from '../../common/SearchBar';
import { ArrowDownTrayIcon, DocumentDuplicateIcon, ChevronDownIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useDocumentApi } from '../../../contexts/ApiContext';
import { useNotificationService } from '../../Notification';
import { downloadStructureJSON, copyStructureToClipboard } from './exportUtils';

interface DocumentToolbarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortDirection: 'asc' | 'desc';
  setSortDirection: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  isAllSelected: boolean;
  toggleSelectAllFolders: () => void;
  totalDocsToDelete: number;
  totalFoldersToDelete: number;
  handleRemoveSelected: () => void;
  deletingDocumentIds: string[];
  deletingFolderIds: string[];
  onUploadFolder: () => void;
  t: (key: string, options?: any) => string;
}

const DocumentToolbar: React.FC<DocumentToolbarProps> = ({
  searchQuery,
  setSearchQuery,
  sortDirection,
  setSortDirection,
  isAllSelected,
  toggleSelectAllFolders,
  totalDocsToDelete,
  totalFoldersToDelete,
  handleRemoveSelected,
  deletingDocumentIds,
  deletingFolderIds,
  onUploadFolder,
  t
}) => {
  // Mobile expand states
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  // Export functionality
  const documentApi = useDocumentApi();
  const notification = useNotificationService();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportDownload = async () => {
    setShowExportMenu(false);
    setIsExporting(true);
    try {
      await downloadStructureJSON(documentApi);
      notification.showSuccess(t('content.documents.export.title'), t('content.documents.export.downloadSuccess'));
    } catch (error) {
      console.error('Export download error:', error);
      notification.showError(t('content.documents.export.title'), t('content.documents.export.downloadError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCopy = async () => {
    setShowExportMenu(false);
    setIsExporting(true);
    try {
      await copyStructureToClipboard(documentApi);
      notification.showSuccess(t('content.documents.export.title'), t('content.documents.export.copySuccess'));
    } catch (error) {
      console.error('Export copy error:', error);
      notification.showError(t('content.documents.export.title'), t('content.documents.export.copyError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleSearchIconClick = () => {
    setIsSearchOpen(true);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  const hasItemsToDelete = totalDocsToDelete > 0 || totalFoldersToDelete > 0;
  const isDeleting = deletingDocumentIds.length > 0 || deletingFolderIds.length > 0;

  const getDeleteButtonText = () => {
    if (isDeleting) {
      return t('content.documents.selection.deleting');
    }
    const foldersText = totalFoldersToDelete > 0
      ? t('content.documents.selection.folders_other', { count: totalFoldersToDelete })
      : '';
    const docsText = totalDocsToDelete > 0
      ? t('content.documents.selection.documents_other', { count: totalDocsToDelete })
      : '';
    const separator = totalFoldersToDelete > 0 && totalDocsToDelete > 0
      ? t('content.documents.selection.and')
      : '';
    return t('content.documents.selection.deleteFoldersAndDocs', {
      foldersText,
      separator,
      docsText
    });
  };

  return (
    <div className='mb-4'>
      {/* Mobile: Search Expanded State */}
      {isSearchOpen && (
        <div className='flex w-full items-center gap-2 sm:hidden'>
          <div className='flex-1'>
            <SearchBar
              placeholder={t('content.documents.searchPlaceholder')}
              value={searchQuery}
              onChange={setSearchQuery}
              className='w-full'
            />
          </div>
          <button
            type='button'
            onClick={handleSearchClose}
            className='flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
            title={t('common.cancel')}
          >
            <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>
      )}

      {/* Mobile: Icon Buttons Row (hidden when search is open) */}
      {!isSearchOpen && (
        <div className='flex items-center justify-between sm:hidden'>
          {/* Left group: buttons */}
          <div className='flex items-center gap-2'>
            {/* Search Icon */}
            <button
              type='button'
              onClick={handleSearchIconClick}
              className='flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-100 dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
              title={t('content.documents.searchPlaceholder')}
            >
              <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
              </svg>
            </button>

            {/* Upload Folder Icon */}
            <button
              type='button'
              onClick={onUploadFolder}
              className='flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-100 dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
              title={t('content.documents.buttons.uploadFolder')}
            >
              <CloudArrowUpIcon className='h-5 w-5' />
            </button>

            {/* Select All Icon */}
            <button
              type='button'
              onClick={toggleSelectAllFolders}
              className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                isAllSelected
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100 dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
              }`}
              title={isAllSelected ? t('content.documents.selection.deselectAll') : t('content.documents.selection.selectAllRootFolders')}
            >
              <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' />
              </svg>
            </button>

            {/* Sort Icon */}
            <button
              type='button'
              onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
              className='flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-100 dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
              title={t('content.documents.sort.title')}
            >
              {sortDirection === 'asc' ? (
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12' />
                </svg>
              ) : (
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4' />
                </svg>
              )}
            </button>

            {/* Export Icon */}
            <button
              type='button'
              onClick={handleExportDownload}
              disabled={isExporting}
              className='flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
              title={t('content.documents.export.title')}
            >
              {isExporting ? (
                <svg className='h-5 w-5 animate-spin' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              ) : (
                <ArrowDownTrayIcon className='h-5 w-5' />
              )}
            </button>
          </div>

          {/* Right: Delete Icon (when items selected) */}
          {hasItemsToDelete && (
            <button
              type='button'
              onClick={handleRemoveSelected}
              disabled={isDeleting}
              className='flex h-9 w-9 items-center justify-center rounded-lg border border-red-300 bg-red-50 text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50 dark:border-red-600 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40'
              title={getDeleteButtonText()}
            >
              {isDeleting ? (
                <svg className='h-5 w-5 animate-spin' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              ) : (
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                </svg>
              )}
            </button>
          )}
        </div>
      )}

      {/* Desktop: Full Toolbar */}
      <div className='hidden flex-col gap-3 sm:flex sm:flex-row sm:items-center sm:justify-between'>
        {/* Left: SearchBar */}
        <div className='flex-1 max-w-md'>
          <SearchBar
            placeholder={t('content.documents.searchPlaceholder')}
            value={searchQuery}
            onChange={setSearchQuery}
            className='w-full'
          />
        </div>

        {/* Right Group: Action Buttons */}
        <div className='flex items-center gap-2 flex-shrink-0'>
          {/* Upload Folder Button */}
          <Button
            onClick={onUploadFolder}
            size='sm'
            variant='primary'
            icon={<CloudArrowUpIcon className='h-4 w-4' />}
          >
            {t('content.documents.buttons.uploadFolder')}
          </Button>

          {/* Nút Chọn Tất Cả */}
          <Button
            variant={isAllSelected ? 'primary' : 'outline'}
            onClick={toggleSelectAllFolders}
            size='sm'
          >
            {isAllSelected
              ? t('content.documents.selection.deselectAll')
              : t('content.documents.selection.selectAllRootFolders')
            }
          </Button>

          {/* Nút Xóa (hiện khi có item được chọn) */}
          {hasItemsToDelete && (
            <Button
              variant='danger'
              onClick={handleRemoveSelected}
              disabled={isDeleting}
              size='sm'
            >
              {isDeleting ? (
                <div className='flex items-center space-x-2'>
                  <svg className='h-4 w-4 animate-spin' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  <span>{t('content.documents.selection.deleting')}</span>
                </div>
              ) : (
                getDeleteButtonText()
              )}
            </Button>
          )}

          {/* Nút Lọc/Sắp Xếp */}
          <Button
            variant='outline'
            onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            size='sm'
            title={t('content.documents.sort.title')}
          >
            {sortDirection === 'asc' ? 'A→Z' : 'Z→A'}
          </Button>

          {/* Export Structure Button */}
          <div ref={exportMenuRef} className='relative'>
            <Button
              variant='outline'
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={isExporting}
              size='sm'
              title={t('content.documents.export.title')}
            >
              {isExporting ? (
                <svg className='h-4 w-4 animate-spin' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              ) : (
                <>
                  <span>{t('content.documents.export.button')}</span>
                  <ChevronDownIcon className='ml-1 h-4 w-4' />
                </>
              )}
            </Button>
            {showExportMenu && (
              <div className='absolute right-0 z-50 mt-2 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-border-dark dark:bg-dark-background-secondary'>
                <button
                  onClick={handleExportDownload}
                  className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
                >
                  <ArrowDownTrayIcon className='h-4 w-4' />
                  <span>{t('content.documents.export.downloadJson')}</span>
                </button>
                <button
                  onClick={handleExportCopy}
                  className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
                >
                  <DocumentDuplicateIcon className='h-4 w-4' />
                  <span>{t('content.documents.export.copyToClipboard')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentToolbar;
